/**
 * Care Router
 * CCH Care - Phase 2-5f
 * Last Modified: 2026-05-13
 *
 * Changes (2026-05-13) — Phase 2 (Shift-Based Access Control):
 * - Added RequireOnShift layout route wrapping the clinical workflow
 *   routes (residents, messages, bedtime, mar, controlled, adl). These
 *   routes are now gated by shift state per Policy v1.0 §4.2: CNAs
 *   without an active or in-grace shift get redirected to /dashboard.
 *   Roles with patients.view_all (Care Supervisors, Care Admins) bypass.
 * - The dashboard index, MorePage, navigation routes, admin routes,
 *   shift history, and help routes remain ungated — they're either the
 *   redirect target, navigation utility, or admin-permission-gated
 *   independently.
 *
 * Changes (2026-04-21) — Feature #11:
 * - Added public routes /forgot-password and /reset-password alongside
 *   the other pre-auth routes. Both sit outside the /dashboard protected
 *   tree so they're reachable without a session.
 *
 * Phase 5f: Added Shift History and Help & Guides routes.
 *           Shift History: /dashboard/shift-history (list)
 *                          /dashboard/shift-history/:shiftId (detail)
 *                          /dashboard/shift-history/:shiftId/adl/:adlRecordId (read-only ADL)
 *           Help & Guides: /dashboard/help
 *           MorePage items for both enabled (disabled: true removed).
 *
 * Dark mode: replaced all hardcoded hex values with COLORS.* tokens
 *            so CSS variables respond to theme switching.
 *
 * ESLint: click-events-have-key-events, no-static-element-interactions
 */

import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import RequireOnShift from './RequireOnShift';
import RequireRole from './RequireRole';
import LandingPage from '../../features/home/LandingPage';
import LoginPage from '../../features/auth/LoginPage';
import RegisterPage from '../../features/auth/RegisterPage';
import ActivatePage from '../../features/auth/ActivatePage';
import ForgotPasswordPage from '../../features/auth/ForgotPasswordPage';
import ResetPasswordPage from '../../features/auth/ResetPasswordPage';
import AppShell from '../layout/AppShell';
import ShiftDashboard from '../../features/dashboard/components/ShiftDashboard';
import SetupWizard from '../../features/admin/components/SetupWizard';
import StaffManagement from '../../features/admin/components/StaffManagement';
import ADLFlowSheetPage from '../../features/adl/ADLFlowSheetPage';
import BedtimeChecklistPage from '../../features/adl/BedtimeChecklistPage';
import MARPage from '../../features/adl/MARPage';
import ControlledSubstancePage from '../../features/adl/ControlledSubstancePage';
import HandoffNotesPage from '../../features/handoff/HandoffNotesPage';
import ResidentsDirectoryPage from '../../features/residents/ResidentsDirectoryPage';
import ReportsPage from '../../features/reports/ReportsPage';
import OrganizationSettingsPage from '../../features/admin/OrganizationSettingsPage';
import ShiftHistoryPage from '../../features/shifts/ShiftHistoryPage';
import ShiftDetailPage from '../../features/shifts/ShiftDetailPage';
import ADLSummaryPage from '../../features/shifts/ADLSummaryPage';
import HelpGuidesPage from '../../features/help/HelpGuidesPage';
import { COLORS } from '../../config/constants';

function TasksPage() {
  return (
    <div style={{ padding: 40, textAlign: 'center', color: COLORS.textMuted }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
      <h2 style={{ color: COLORS.text }}>Task Queue</h2>
      <p>ADL tasks sorted by urgency &mdash; coming in Phase 3.</p>
    </div>
  );
}

function MorePage() {
  const navigate = useNavigate();
  const menuItems = [
    { icon: '🏗️', label: 'Facility Setup',        sub: 'Units, rooms, beds, residents, shifts',        path: '/dashboard/setup'          },
    { icon: '👥', label: 'Staff Management',       sub: 'Invite and manage CNA accounts',               path: '/dashboard/staff'          },
    { icon: '🌙', label: 'Bedtime Checklist',      sub: 'Evening care and resident wellbeing',          path: '/dashboard/bedtime'        },
    { icon: '💊', label: 'Medications (MAR)',       sub: 'Medication orders and administration',         path: '/dashboard/mar'            },
    { icon: '🔐', label: 'Controlled Substances',  sub: 'Counts, waste, and discrepancy reporting',     path: '/dashboard/controlled'     },
    { icon: '📊', label: 'Reports',                sub: 'Shift summaries, compliance',                  path: '/dashboard/reports'        },
    { icon: '⚙️', label: 'Organization Settings', sub: 'Facility address, billing contact, timezone',  path: '/dashboard/settings'       },
    { icon: '📋', label: 'Shift History',          sub: 'Past shifts and documentation',                path: '/dashboard/shift-history'  },
    { icon: '❓', label: 'Help & Guides',          sub: 'Quick reference, CNA guide, troubleshooting',  path: '/dashboard/help'           },
  ];

  return (
    <div style={{ padding: 16, paddingBottom: 80 }}>
      <h2 style={{ margin: '0 0 16px', fontSize: 18, color: COLORS.text }}>More</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {menuItems.map((item, i) => (
          <div
            key={i}
            role="button"
            tabIndex={0}
            onClick={() => item.path && navigate(item.path)}
            onKeyDown={e => {
              if ((e.key === 'Enter' || e.key === ' ') && item.path) {
                e.preventDefault();
                navigate(item.path);
              }
            }}
            style={{
              background: COLORS.surface, borderRadius: 10, padding: '14px 16px',
              border: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: 12,
              cursor: 'pointer',
            }}
          >
            <span style={{ fontSize: 24 }}>{item.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{item.label}</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted }}>{item.sub}</div>
            </div>
            <span style={{ color: COLORS.textMuted }}>›</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SetupPage() {
  const navigate = useNavigate();
  return (
    <div style={{ paddingBottom: 80 }}>
      <div style={{
        padding: '12px 16px', background: COLORS.surface,
        borderBottom: `1px solid ${COLORS.border}`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <button
          onClick={() => navigate('/dashboard/more')}
          style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: COLORS.primary, padding: '4px 8px' }}
        >
          ← Back
        </button>
        <span style={{ fontSize: 15, fontWeight: 600, color: COLORS.text }}>Facility Setup</span>
      </div>
      <SetupWizard onComplete={() => navigate('/dashboard')} />
    </div>
  );
}

function AdminPage() {
  return (
    <div style={{ padding: 40, textAlign: 'center', color: COLORS.textMuted }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>⚙️</div>
      <h2 style={{ color: COLORS.text }}>Admin</h2>
      <p>Phase 4 &mdash; Coming soon</p>
    </div>
  );
}

export default function CareRouter() {
  return (
    <Routes>
      <Route path="/"                 element={<LandingPage />} />
      <Route path="/login"            element={<LoginPage />} />
      <Route path="/get-started"      element={<RegisterPage />} />
      <Route path="/register"         element={<RegisterPage />} />
      <Route path="/activate"         element={<ActivatePage />} />
      <Route path="/forgot-password"  element={<ForgotPasswordPage />} />
      <Route path="/reset-password"   element={<ResetPasswordPage />} />

      <Route path="/dashboard" element={
        <ProtectedRoute>
          <AppShell />
        </ProtectedRoute>
      }>
        {/* Always-accessible routes — dashboard is the redirect target
            for the shift gate, navigation and reference routes don't
            require an active shift, and admin routes have their own
            permission gates. */}
        <Route index                     element={<ShiftDashboard />} />
        <Route path="tasks"              element={<TasksPage />} />
        <Route path="more"               element={<MorePage />} />
        <Route path="setup"              element={<SetupPage />} />
        <Route path="staff"              element={<StaffManagement />} />
        <Route path="reports"            element={<ReportsPage />} />
        <Route path="settings"           element={<OrganizationSettingsPage />} />
        <Route path="shift-history"                           element={<ShiftHistoryPage />} />
        <Route path="shift-history/:shiftId"                  element={<ShiftDetailPage />} />
        <Route path="shift-history/:shiftId/adl/:adlRecordId" element={<ADLSummaryPage />} />
        <Route path="help"               element={<HelpGuidesPage />} />

        {/* Clinical workflow routes — require active or in-grace shift
            for view_assigned roles (CNA). Roles with view_all (Care
            Supervisor, Care Admin) bypass per Policy v1.0 §4.8. */}
        <Route element={<RequireOnShift />}>
          <Route element={<RequireRole allow={['Care Supervisor', 'Care Admin', 'Physician', 'Nurse']} />}>
            <Route path="residents"        element={<ResidentsDirectoryPage />} />
          </Route>
          <Route path="messages"           element={<HandoffNotesPage />} />
          <Route path="bedtime"            element={<BedtimeChecklistPage />} />
          <Route path="mar"                element={<MARPage />} />
          <Route path="controlled"         element={<ControlledSubstancePage />} />
          <Route path="adl"                element={<ADLFlowSheetPage />} />
          <Route path="adl/:patientId"     element={<ADLFlowSheetPage />} />
        </Route>

        <Route path="admin/*" element={
          <ProtectedRoute permission="facilities.manage">
            <AdminPage />
          </ProtectedRoute>
        } />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
